import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, X, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight,
  CheckCircle, Clock, XCircle, ExternalLink, ArrowUpDown, Download,
} from 'lucide-react';

import { getApiBookingRouteId, getUserFacingBookingId } from '../../utils/userFacingBookingId';

/**
 * TransactionsTable — reusable ledger-style table for agent Wallet / Credit.
 *
 * Props:
 *   fetchPage({page, limit, search, type, status, date_from, date_to, sort, ...extra})
 *     -> Promise<{transactions, total, pages, ledger}>
 *   columns — array of {key, label, render?, align?, footer?(ledger), mobileSpan?}
 *   typeOptions, statusOptions — [{value, label}]
 *   extraFilters — array of ReactNodes rendered into the filter bar
 *   exportUrl — optional; renders "Statement" (CSV) button wired to the filters
 *   ledgerLabels — override for Wallet vs Credit terminology
 */
export const TransactionsTable = ({
  fetchPage,
  columns,
  typeOptions = [],
  statusOptions = [],
  extraFilters = [],
  initialLimit = 20,
  emptyState = null,
  testidPrefix = 'txn',
  expandRow = null,
  refreshKey = 0,
  ledgerLabels = null,
  exportUrl = null,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(initialLimit);
  const [sort, setSort] = useState('desc');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  useEffect(() => { setPage(1); }, [type, status, dateFrom, dateTo, limit, sort]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchPage({
          page, limit, search: debouncedSearch, type, status,
          date_from: dateFrom, date_to: dateTo, sort,
        });
        if (cancel) return;
        setRows(res.transactions || []);
        setTotal(res.total || 0);
        setPages(res.pages || res.total_pages || 0);
        setLedger(res.ledger || null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, debouncedSearch, type, status, dateFrom, dateTo, sort, refreshKey]);

  const clearFilters = () => {
    setSearch(''); setType(''); setStatus(''); setDateFrom(''); setDateTo('');
  };
  const hasActiveFilters = !!(search || type || status || dateFrom || dateTo);

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const exportCsv = () => {
    if (!exportUrl) return;
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (type) params.set('transaction_type', type);
    if (status) params.set('status', status);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    params.set('sort', 'asc');
    window.open(`${exportUrl}?${params.toString()}`, '_blank');
  };

  const LABELS = ledgerLabels || {
    opening: 'Opening Balance', closing: 'Closing Balance',
    debit: 'Total Debit (Dr)', credit: 'Total Credit (Cr)',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
         data-testid={`${testidPrefix}-table-wrapper`}>
      {/* Ledger header */}
      {ledger && (
        <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100"
             data-testid={`${testidPrefix}-ledger-header`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <LedgerStat
              label={LABELS.opening}
              value={ledger.opening_balance}
              subLabel={ledger.opening_date ? new Date(ledger.opening_date).toLocaleDateString('en-IN') : null}
              tone="neutral"
            />
            <LedgerStat
              label={LABELS.debit}
              value={ledger.total_debit}
              sign="-"
              tone="debit"
              subLabel={`${ledger.txn_count || 0} txns`}
            />
            <LedgerStat
              label={LABELS.credit}
              value={ledger.total_credit}
              sign="+"
              tone="credit"
            />
            <LedgerStat
              label={LABELS.closing}
              value={ledger.closing_balance}
              subLabel={ledger.closing_date ? new Date(ledger.closing_date).toLocaleDateString('en-IN') : null}
              tone="primary"
              bold
            />
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="p-4 border-b border-gray-100 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search transaction ID, booking, description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
            data-testid={`${testidPrefix}-search`}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              data-testid={`${testidPrefix}-search-clear`}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {typeOptions.length > 0 && (
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
            data-testid={`${testidPrefix}-type-filter`}
          >
            <option value="">All types</option>
            {typeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}

        {statusOptions.length > 0 && (
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
            data-testid={`${testidPrefix}-status-filter`}
          >
            <option value="">All statuses</option>
            {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}

        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 text-xs hidden md:inline">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
            data-testid={`${testidPrefix}-date-from`}
          />
          <span className="text-gray-500 text-xs hidden md:inline">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
            data-testid={`${testidPrefix}-date-to`}
          />
        </div>

        {extraFilters.map((node, i) => <React.Fragment key={i}>{node}</React.Fragment>)}

        <button
          onClick={() => setSort(sort === 'desc' ? 'asc' : 'desc')}
          className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1"
          title={sort === 'desc' ? 'Latest first (passbook view)' : 'Oldest first (running ledger)'}
          data-testid={`${testidPrefix}-sort-toggle`}
        >
          <ArrowUpDown size={14} />
          {sort === 'desc' ? 'Latest first' : 'Oldest first'}
        </button>

        {exportUrl && (
          <button
            onClick={exportCsv}
            className="px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1"
            data-testid={`${testidPrefix}-export-csv`}
          >
            <Download size={14} /> Statement
          </button>
        )}

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg font-medium flex items-center gap-1"
            data-testid={`${testidPrefix}-clear-filters`}
          >
            <X size={14} /> Clear
          </button>
        )}
      </div>

      <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 flex items-center justify-between border-b border-gray-100">
        <span data-testid={`${testidPrefix}-result-count`}>
          {loading ? 'Loading…' : total === 0 ? 'No transactions' :
            `Showing ${start}–${end} of ${total.toLocaleString()} ${hasActiveFilters ? '(filtered)' : ''}`}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">Rows per page:</span>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="px-2 py-1 border border-gray-200 rounded text-xs bg-white"
            data-testid={`${testidPrefix}-page-size`}
          >
            {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Desktop table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              {columns.map((col) => (
                <th key={col.key}
                    className={`py-3 px-4 font-semibold ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}>
                  {col.label}
                </th>
              ))}
              {expandRow && <th className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-t border-gray-100 animate-pulse">
                  {columns.map((c) => (
                    <td key={c.key} className="py-4 px-4">
                      <div className="h-3 bg-gray-100 rounded" />
                    </td>
                  ))}
                  {expandRow && <td />}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (expandRow ? 1 : 0)} className="py-12 text-center">
                  {emptyState || (
                    <div className="text-gray-500" data-testid={`${testidPrefix}-empty`}>
                      <p className="font-medium">No transactions found</p>
                      {hasActiveFilters && (
                        <button onClick={clearFilters}
                          className="text-teal-600 hover:text-teal-700 text-sm mt-1 underline">
                          Clear filters
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <React.Fragment key={row.id || idx}>
                  <tr
                    className={`border-t border-gray-100 hover:bg-gray-50 ${expandRow ? 'cursor-pointer' : ''}`}
                    onClick={() => expandRow && setExpanded(expanded === idx ? null : idx)}
                    data-testid={`${testidPrefix}-row-${idx}`}
                  >
                    {columns.map((col) => (
                      <td key={col.key}
                          className={`py-3 px-4 ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'} ${col.className || ''}`}>
                        {col.render ? col.render(row) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                    {expandRow && (
                      <td className="py-3 px-2 text-gray-400">
                        <ChevronRight
                          size={16}
                          className={`transition-transform ${expanded === idx ? 'rotate-90' : ''}`}
                        />
                      </td>
                    )}
                  </tr>
                  {expandRow && expanded === idx && (
                    <tr className="bg-gray-50 border-t border-gray-100">
                      <td colSpan={columns.length + 1} className="p-4">
                        {expandRow(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
          {/* Totals footer */}
          {ledger && rows.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200 font-semibold text-xs text-gray-700">
              <tr>
                {columns.map((col) => {
                  let content = '';
                  if (col.footer) content = col.footer(ledger);
                  return (
                    <td key={col.key}
                        className={`py-3 px-4 ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}>
                      {content}
                    </td>
                  );
                })}
                {expandRow && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-100">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="p-4 animate-pulse">
              <div className="h-3 bg-gray-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-2/3" />
            </div>
          ))
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid={`${testidPrefix}-empty-mobile`}>
            <p className="font-medium">No transactions found</p>
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="text-teal-600 text-sm mt-1 underline">Clear filters</button>
            )}
          </div>
        ) : rows.map((row, idx) => (
          <div key={row.id || idx} className="p-4" data-testid={`${testidPrefix}-card-${idx}`}
               onClick={() => expandRow && setExpanded(expanded === idx ? null : idx)}>
            <div className="flex items-start gap-2 flex-wrap">
              {columns.map((col) => (
                <div key={col.key} className={col.mobileSpan === 'full' ? 'w-full' : 'flex-1 min-w-[45%]'}>
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">{col.label}</p>
                  <div className={`text-sm ${col.className || ''}`}>
                    {col.render ? col.render(row) : (row[col.key] ?? '—')}
                  </div>
                </div>
              ))}
            </div>
            {expandRow && expanded === idx && (
              <div className="mt-3 pt-3 border-t border-gray-200">{expandRow(row)}</div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="p-4 border-t border-gray-100 flex items-center justify-between text-sm">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 flex items-center gap-1"
            data-testid={`${testidPrefix}-prev-page`}
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <span className="text-gray-600">
            Page <strong>{page}</strong> of <strong>{pages}</strong>
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 flex items-center gap-1"
            data-testid={`${testidPrefix}-next-page`}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

const LedgerStat = ({ label, value, subLabel, sign = '', tone = 'neutral', bold }) => {
  const colors = {
    neutral: 'text-gray-700',
    debit:   'text-red-600',
    credit:  'text-emerald-600',
    primary: 'text-[#0C1D36]',
  };
  const n = Number(value || 0);
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">{label}</p>
      <p className={`${bold ? 'text-xl font-bold' : 'text-lg font-semibold'} ${colors[tone] || colors.neutral}`}>
        {sign}₹{n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </p>
      {subLabel && <p className="text-[10px] text-gray-400 mt-0.5">{subLabel}</p>}
    </div>
  );
};

// ── Cell renderers ──────────────────────────────────────────────────────────
export const TxnStatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const styles = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    completed: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-700',
    failed: 'bg-red-100 text-red-700',
    open: 'bg-blue-100 text-blue-700',
    closed: 'bg-gray-100 text-gray-700',
    overdue: 'bg-red-100 text-red-700',
  };
  const icons = { pending: Clock, approved: CheckCircle, completed: CheckCircle,
                  rejected: XCircle, failed: XCircle, cancelled: XCircle };
  const Icon = icons[s] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[s] || 'bg-gray-100 text-gray-700'}`}>
      <Icon size={12} /> {status || '—'}
    </span>
  );
};

export const DebitCell = ({ amount }) => {
  const n = Number(amount || 0);
  if (!n) return <span className="text-gray-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1 font-semibold text-red-600">
      <ArrowDownRight size={14} /> ₹{n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
    </span>
  );
};

export const CreditCell = ({ amount }) => {
  const n = Number(amount || 0);
  if (!n) return <span className="text-gray-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1 font-semibold text-emerald-600">
      <ArrowUpRight size={14} /> ₹{n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
    </span>
  );
};

export const BalanceCell = ({ value }) => {
  const v = value == null ? null : Number(value);
  if (v == null) return <span className="text-gray-400">—</span>;
  return (
    <span className="font-mono text-gray-800">
      ₹{v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
    </span>
  );
};

// Legacy exports kept for any other callers
export const TxnTypeCell = ({ type, positiveLabel = 'Credit', negativeLabel = 'Debit' }) => {
  const t = (type || '').toLowerCase();
  const isPositive = t === 'credit' || t === 'payment' || t === 'refund';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
      {isPositive ? positiveLabel : negativeLabel}
    </span>
  );
};

export const TxnAmountCell = ({ amount, type }) => {
  const t = (type || '').toLowerCase();
  const isPositive = t === 'credit' || t === 'payment' || t === 'refund';
  const n = Number(amount || 0);
  return (
    <span className={`font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {isPositive ? '+' : '-'}₹{n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
    </span>
  );
};

export const BookingLink = ({ booking_id, booking }) => {
  const entity = booking && typeof booking === 'object' ? { ...booking, booking_id: booking.booking_id || booking_id } : { booking_id };
  const hrefId = getApiBookingRouteId(entity);
  const display = getUserFacingBookingId(entity);
  if (!hrefId && !booking_id) return <span className="text-gray-400">—</span>;
  const label = display !== '—' ? display : (booking_id && !String(booking_id).trim().startsWith('TJ') ? booking_id : '—');
  if (!hrefId) {
    return <span className="text-gray-600 font-mono text-xs">{label}</span>;
  }
  return (
    <a href={`/booking-details/${hrefId}`}
       className="text-teal-600 hover:text-teal-700 inline-flex items-center gap-1 text-xs font-mono"
       onClick={(e) => e.stopPropagation()}>
      {label} <ExternalLink size={12} />
    </a>
  );
};

export const useStableCallback = (fn) => {
  const ref = useRef(fn);
  ref.current = fn;
  return useMemo(() => ((...args) => ref.current(...args)), []);
};

export default TransactionsTable;
